import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Copy, RefreshCw, Ban, Loader2, Key, RotateCcw, AlertCircle, CheckCircle2, Smartphone,
} from "lucide-react";

export default function AdminSdkClients() {
  const [clients, setClients] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", partner_id: "_none", description: "" });
  const [creating, setCreating] = useState(false);
  const [credsModal, setCredsModal] = useState(null); // {client_id, client_secret} — shown once

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sdk-clients");
      setClients(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  const loadPartners = async () => {
    try {
      const { data } = await api.get("/admin/users");
      const items = Array.isArray(data) ? data : (data.items || []);
      setPartners(items.filter((u) => u.role === "partner"));
    } catch { /* ok */ }
  };

  useEffect(() => { load(); loadPartners(); }, []);

  const submitCreate = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setCreating(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
      };
      if (form.partner_id && form.partner_id !== "_none") payload.partner_id = form.partner_id;
      const { data } = await api.post("/admin/sdk-clients", payload);
      setCreateOpen(false);
      setForm({ name: "", partner_id: "_none", description: "" });
      setCredsModal({ client_id: data.client.client_id, client_secret: data.client_secret, name: data.client.name });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create");
    } finally { setCreating(false); }
  };

  const rotate = async (clientId) => {
    if (!window.confirm("Rotate the secret? The current secret will stop working immediately.")) return;
    try {
      const { data } = await api.post(`/admin/sdk-clients/${clientId}/rotate`);
      setCredsModal({ client_id: data.client_id, client_secret: data.client_secret, name: "Rotated", isRotation: true });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rotate failed");
    }
  };

  const revoke = async (clientId) => {
    if (!window.confirm("Revoke this SDK client? Apps using its credentials will fail with HTTP 401 until you create a new client.")) return;
    try {
      await api.post(`/admin/sdk-clients/${clientId}/revoke`);
      toast.success("Revoked");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Revoke failed");
    }
  };

  const copy = async (s) => {
    try { await navigator.clipboard.writeText(s); toast.success("Copied"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Admin</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">SDK Clients</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Issue OAuth client credentials for partner mobile apps. Each client uses
            <code className="font-mono text-[10px] bg-accent px-1 mx-0.5 rounded">/api/oauth/token</code> with
            <code className="font-mono text-[10px] bg-accent px-1 mx-0.5 rounded">grant_type=client_credentials</code>
            to obtain a 15-minute JWT, then calls partner APIs on behalf of the bound partner.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-sdk-client-btn">
          <Plus className="h-4 w-4 mr-1" /> Issue credentials
        </Button>
      </div>

      <div className="mt-6 surface-card p-4 border-l-4 border-l-primary" data-testid="discovery-card">
        <div className="flex items-start gap-3">
          <Smartphone className="h-5 w-5 text-primary mt-0.5" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="font-display font-semibold text-sm">Discovery URL · Flutter SDK init</div>
            <p className="text-xs text-muted-foreground mt-1">
              Partners' Flutter apps initialise with this URL. The discovery doc auto-updates when you deploy —
              no SDK upgrade needed in the app.
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <code className="font-mono text-xs bg-stone-950 text-stone-100 px-3 py-1.5 rounded flex-1 break-all">
                {window.location.origin}/api/.well-known/payflex.json
              </code>
              <Button variant="outline" size="sm" onClick={() => copy(`${window.location.origin}/api/.well-known/payflex.json`)} data-testid="copy-discovery-url">
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(`${window.location.origin}/api/.well-known/payflex.json`, "_blank")}>
                Preview JSON
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 surface-card overflow-x-auto" data-testid="sdk-clients-table">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-accent/30">
            <tr>
              <th className="p-3">Name</th>
              <th className="p-3">Client ID</th>
              <th className="p-3">Partner</th>
              <th className="p-3">Status</th>
              <th className="p-3">Last used</th>
              <th className="p-3">Calls / fails</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 && !loading && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                <Key className="h-6 w-6 mx-auto opacity-40 mb-2" />
                No SDK clients yet. Issue one above.
              </td></tr>
            )}
            {clients.map((c) => {
              const isRevoked = !!c.revoked_at;
              return (
                <tr key={c.client_id} className="border-t border-border hover:bg-accent/20" data-testid={`sdk-client-row-${c.client_id}`}>
                  <td className="p-3">
                    <div className="font-medium">{c.name}</div>
                    {c.description && <div className="text-[11px] text-muted-foreground mt-0.5">{c.description}</div>}
                  </td>
                  <td className="p-3 font-mono text-xs">
                    <div className="flex items-center gap-1">
                      <span className="break-all">{c.client_id}</span>
                      <Button variant="ghost" size="sm" onClick={() => copy(c.client_id)} className="h-6 w-6 p-0">
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                  <td className="p-3 text-xs">
                    {c.partner_id ? (
                      partners.find((p) => p.id === c.partner_id)?.full_name || c.partner_id.slice(0, 8) + "…"
                    ) : (
                      <span className="text-muted-foreground italic">Standalone</span>
                    )}
                  </td>
                  <td className="p-3">
                    {isRevoked ? (
                      <Badge variant="outline" className="border-rose-300 text-rose-700 bg-rose-50 text-[10px]">
                        <Ban className="h-3 w-3 mr-1" /> revoked
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> active
                      </Badge>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground font-mono">
                    {c.last_used_at ? c.last_used_at.slice(0, 16).replace("T", " ") : <em>never</em>}
                  </td>
                  <td className="p-3 text-xs font-mono">
                    <span className="text-emerald-700">{c.successful_calls || 0}</span>
                    <span className="text-muted-foreground"> / </span>
                    <span className="text-rose-700">{c.failed_attempts || 0}</span>
                  </td>
                  <td className="p-3">
                    {!isRevoked && (
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => rotate(c.client_id)} title="Rotate secret" data-testid={`rotate-${c.client_id}`}>
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => revoke(c.client_id)} className="text-destructive hover:bg-destructive/10" title="Revoke" data-testid={`revoke-${c.client_id}`}>
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-dialog">
          <DialogHeader>
            <DialogTitle>Issue SDK credentials</DialogTitle>
            <DialogDescription>The secret is shown ONCE. Save it in your CI / app secret store immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme Mobile App · prod" data-testid="form-name" />
            </div>
            <div>
              <Label>Bind to partner <span className="text-muted-foreground text-[10px]">(optional)</span></Label>
              <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                <SelectTrigger className="mt-1" data-testid="form-partner"><SelectValue placeholder="Standalone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Standalone (no partner)</SelectItem>
                  {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name} · {p.email}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">If bound, the SDK acts on behalf of this partner — wallet, KYC, BBPS calls run as them.</p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Production iOS / Android app · v1.2 onwards" data-testid="form-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate} disabled={creating} data-testid="form-submit">
              {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Issue credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials reveal modal */}
      <Dialog open={!!credsModal} onOpenChange={(o) => { if (!o) setCredsModal(null); }}>
        <DialogContent className="max-w-lg" data-testid="creds-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-warning" />
              {credsModal?.isRotation ? "Secret rotated" : "Save these credentials"}
            </DialogTitle>
            <DialogDescription>
              The client secret is shown ONCE. FlexPay stores only the bcrypt hash — there is no way to retrieve this value later.
              Copy it into your secret store before closing this dialog.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Client ID</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-stone-950 text-stone-100 p-3 rounded break-all">{credsModal?.client_id}</code>
                <Button size="sm" variant="outline" onClick={() => copy(credsModal?.client_id || "")} data-testid="copy-client-id">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Client Secret · shown once</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-stone-950 text-stone-100 p-3 rounded break-all">{credsModal?.client_secret}</code>
                <Button size="sm" variant="outline" onClick={() => copy(credsModal?.client_secret || "")} data-testid="copy-client-secret">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs">
              <div className="font-medium">Flutter SDK quick-start</div>
              <pre className="mt-2 font-mono text-[11px] bg-stone-950 text-stone-100 p-2 rounded overflow-auto">{`final sdk = FlexPay(
  clientId: '${credsModal?.client_id}',
  clientSecret: '${credsModal?.client_secret?.slice(0, 12)}...',
);
await sdk.init();`}</pre>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredsModal(null)} data-testid="creds-acknowledge">I've saved them</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
